import { config } from '@/lib/config';
import type { ApiError, AuthTokens } from '@/types/api';
import { tokenStore } from './tokens';

export class ApiClientError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details?: Record<string, unknown>;

  constructor(status: number, payload: ApiError) {
    super(payload.message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = payload.code;
    this.details = payload.details as Record<string, unknown> | undefined;
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body' | 'headers'> {
  body?: unknown;
  headers?: Record<string, string>;
  searchParams?: Record<string, string | number | boolean | undefined>;
  skipAuth?: boolean;
  skipRefresh?: boolean;
}

type AuthEventType = 'signed-in' | 'signed-out';
const authListeners: Array<(type: AuthEventType) => void> = [];

export function onAuthEvent(listener: (type: AuthEventType) => void): () => void {
  authListeners.push(listener);
  return () => {
    const i = authListeners.indexOf(listener);
    if (i >= 0) authListeners.splice(i, 1);
  };
}

function emitAuthEvent(type: AuthEventType): void {
  for (const l of authListeners) l(type);
}

function buildUrl(path: string, searchParams?: RequestOptions['searchParams']): string {
  const base = config.apiUrl.replace(/\/$/, '');
  const url = new URL(`${base}${path.startsWith('/') ? path : `/${path}`}`);
  if (searchParams) {
    for (const [key, value] of Object.entries(searchParams)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) return false;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        tokenStore.clear();
        emitAuthEvent('signed-out');
        return false;
      }
      const payload = (await response.json()) as { data?: AuthTokens } | AuthTokens;
      const tokens = 'data' in payload && payload.data ? payload.data : (payload as AuthTokens);
      tokenStore.set({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        expiresAt: tokens.expiresAt,
      });
      return true;
    } catch {
      tokenStore.clear();
      emitAuthEvent('signed-out');
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function parseError(response: Response): Promise<ApiError> {
  const text = await response.text();
  if (!text) return { code: 'unknown', message: response.statusText || 'Unexpected error' };
  try {
    const payload = JSON.parse(text) as { error?: ApiError } | ApiError;
    if ('error' in payload && payload.error) return payload.error;
    return payload as ApiError;
  } catch {
    return { code: 'unknown', message: response.statusText || 'Unexpected error' };
  }
}

async function unwrap<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (!text) return undefined as T;
  const parsed = JSON.parse(text) as { data: T } | T;
  if (parsed && typeof parsed === 'object' && 'data' in (parsed as object)) {
    return (parsed as { data: T }).data;
  }
  return parsed as T;
}

async function executeRequest(
  url: string,
  init: RequestInit,
  skipAuth: boolean,
): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!skipAuth) {
    const access = tokenStore.getAccessToken();
    if (access) headers.set('Authorization', `Bearer ${access}`);
  }
  return fetch(url, { ...init, headers });
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, searchParams, skipAuth, skipRefresh, ...init } = options;
  const url = buildUrl(path, searchParams);

  const baseHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...headers,
  };

  const request: RequestInit = {
    ...init,
    headers: baseHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };

  let response = await executeRequest(url, request, Boolean(skipAuth));

  if (response.status === 401 && !skipAuth && !skipRefresh && tokenStore.hasRefresh()) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      response = await executeRequest(url, request, false);
    }
  }

  if (!response.ok) throw new ApiClientError(response.status, await parseError(response));
  return unwrap<T>(response);
}
